(() => {
  'use strict';
  if (window.__BigsmallShortlist) return;
  window.__BigsmallShortlist = true;

  const STORAGE_KEY = 'bigsmall-product-shortlist-v1';
  const SETTINGS_KEY = 'bigsmall-product-shortlist-settings-v1';
  const config = document.querySelector('[data-bs-shortlist-config]');
  const shortlistUrl = config?.dataset.shortlistUrl || '/pages/my-shortlist';
  const shopUrl = config?.dataset.shopUrl || window.location.origin;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const pendingButtons = new WeakSet();
  let products = [];
  let settings = {
    clientName: '',
    proposalQuantity: null,
    proposalTitle: 'Corporate Gift Shortlist',
    adjustmentMode: 'none',
    adjustmentValue: 0,
    includePrices: true,
    selectedImages: {},
    resellerMode: false,
    resellerLogo: null
  };
  let toastTimer = 0;
  let scanQueued = false;

  const safeParse = (value, fallback) => {
    try { return JSON.parse(value) ?? fallback; } catch (_) { return fallback; }
  };
  const esc = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]);
  const normalizeImage = (url) => {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    if (url.startsWith('/')) return `${shopUrl}${url}`;
    return url;
  };
  const productPath = (url) => {
    try { return new URL(url, location.origin).pathname.match(/\/products\/[^/?#]+/)?.[0] || ''; }
    catch (_) { return ''; }
  };
  const canonicalProductUrl = (url) => {
    const path = productPath(url);
    return path ? `${shopUrl}${path}` : String(url || '');
  };
  const productKey = (url) => productPath(url).split('/').pop() || String(url || '');
  const indexByIdentity = (id, url) => {
    const key = productKey(url);
    return products.findIndex((product) => (id && String(product.id) === String(id)) || (key && productKey(product.url) === key));
  };
  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };
  const integerOrNull = (value) => {
    const number = numberOrNull(value);
    return number === null ? null : Math.max(1, Math.round(number));
  };
  const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString('en-IN');
  const money = (value) => `Rs. ${formatNumber(value)}`;

  function bytesToBase64Url(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function base64UrlToBytes(value) {
    let base64 = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    base64 += '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  async function gzipCompress(text) {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  async function gzipDecompress(bytes) {
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
    return new TextDecoder().decode(await new Response(stream).arrayBuffer());
  }

  async function encodeState(obj) {
    const json = JSON.stringify(obj);
    if (window.CompressionStream) {
      try {
        return `z${bytesToBase64Url(await gzipCompress(json))}`;
      } catch (error) {
        console.warn('Compression unavailable, sharing an uncompressed link', error);
      }
    }
    return `p${bytesToBase64Url(new TextEncoder().encode(json))}`;
  }

  async function decodeState(value) {
    const marker = String(value || '').charAt(0);
    const bytes = base64UrlToBytes(String(value || '').slice(1));
    if (marker === 'z') return JSON.parse(await gzipDecompress(bytes));
    return JSON.parse(new TextDecoder().decode(bytes));
  }

  function migrateProduct(raw) {
    const product = raw && typeof raw === 'object' ? raw : {};
    return {
      id: product.id || product.handle || productKey(product.url),
      handle: product.handle || productKey(product.url),
      title: product.title || 'Product',
      sku: product.sku || '',
      url: canonicalProductUrl(product.url || ''),
      price: Number(product.price) || 0,
      images: Array.isArray(product.images) ? product.images.map(normalizeImage).filter(Boolean).slice(0, 5) : [],
      customPrice: numberOrNull(product.customPrice)
    };
  }

  function load() {
    const savedProducts = safeParse(localStorage.getItem(STORAGE_KEY), []);
    const legacyProducts = Array.isArray(savedProducts) ? savedProducts : [];
    products = legacyProducts.map(migrateProduct).filter((product) => product.id && product.url);
    settings = Object.assign(settings, safeParse(localStorage.getItem(SETTINGS_KEY), {}));
    if (!settings.selectedImages || typeof settings.selectedImages !== 'object') settings.selectedImages = {};
    settings.proposalQuantity = integerOrNull(settings.proposalQuantity);
    if (settings.proposalQuantity === null) {
      const legacyQuantity = legacyProducts.map((product) => integerOrNull(product?.customQuantity)).find((value) => value !== null);
      if (legacyQuantity !== undefined) settings.proposalQuantity = legacyQuantity;
    }
  }

  async function applySharedStateFromUrl() {
    const params = new URLSearchParams(location.search);
    const shared = params.get('share');
    if (!shared) return false;
    const status = document.querySelector('[data-bs-pdf-status]');
    try {
      const payload = await decodeState(shared);
      if (!payload || !Array.isArray(payload.p) || !payload.p.length) return false;
      if (status) status.textContent = 'Loading shared shortlist…';
      const hydrated = await Promise.all(payload.p.map(async (item) => {
        try {
          const url = `${shopUrl}/products/${item.h}`;
          const product = await fetchProduct(url);
          product.customPrice = numberOrNull(item.c);
          return product;
        } catch (error) {
          console.warn('Could not load shared product', item?.h, error);
          return null;
        }
      }));
      products = hydrated.filter(Boolean);
      const s = payload.s || {};
      settings = {
        clientName: s.cn || '',
        proposalQuantity: integerOrNull(s.pq),
        proposalTitle: s.pt || 'Corporate Gift Shortlist',
        adjustmentMode: 'none',
        adjustmentValue: 0,
        includePrices: s.ip !== false,
        selectedImages: {},
        resellerMode: false,
        resellerLogo: null
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      history.replaceState(null, '', location.pathname);
      if (status) status.textContent = '';
      return true;
    } catch (error) {
      console.error('Unable to load shared shortlist', error);
      if (status) status.textContent = 'Unable to open this shared link. Please ask for a new one.';
      return false;
    }
  }

  function persistProducts({ render = true } = {}) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    syncButtons();
    syncCounts();
    syncBulkControls();
    if (render) renderShortlistPage();
  }

  function persistSettings({ updatePrices = false } = {}) {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    syncCounts();
    if (updatePrices) updatePriceDisplays();
  }

  function effectivePrice(product) {
    if (product.customPrice !== null) return Number(product.customPrice) || 0;
    const base = Number(product.price) || 0;
    const adjustment = Number(settings.adjustmentValue) || 0;
    if (settings.adjustmentMode === 'percent') return base + (base * adjustment / 100);
    if (settings.adjustmentMode === 'fixed') return base + adjustment;
    if (settings.adjustmentMode === 'percent_discount') return Math.max(0, base - (base * adjustment / 100));
    if (settings.adjustmentMode === 'fixed_discount') return Math.max(0, base - adjustment);
    return base;
  }

  const iconBookmark = () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 4.5h11v15l-5.5-3.4-5.5 3.4z"/><path d="M12 7.5v5M9.5 10h5"/></svg>';
  const iconCheck = () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4 10-10"/></svg>';
  const iconStack = () => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3.5 8.5 4.9-8.5 4.9-8.5-4.9L12 3.5Z"/><path d="m3.5 12.5 8.5 4.9 8.5-4.9"/><path d="m3.5 16.9 8.5 4.9 8.5-4.9"/></svg>';

  function buttonMarkup(url, context) {
    const added = indexByIdentity('', url) > -1;
    return `<button type="button" class="bs-shortlist-add${added ? ' is-added' : ''}" data-bs-add-url="${esc(url)}" data-bs-context="${esc(context || 'card')}" aria-pressed="${added}">${added ? iconCheck() : iconBookmark()}<span>${added ? 'Shortlisted' : 'Add to Shortlist'}</span></button>`;
  }

  function injectCardControls(scope = document) {
    const cards = [];
    if (scope instanceof Element && scope.matches('.product-card-wrapper,.card-wrapper')) cards.push(scope);
    if (scope.querySelectorAll) cards.push(...scope.querySelectorAll('.product-card-wrapper:not([data-bs-shortlist-ready]),.card-wrapper:not([data-bs-shortlist-ready])'));
    cards.forEach((wrapper) => {
      if (wrapper.dataset.bsShortlistReady) return;
      const link = wrapper.querySelector('.card__heading a[href*="/products/"],a[href*="/products/"]');
      const target = wrapper.querySelector('.card__inner') || wrapper.querySelector('.card') || wrapper;
      if (!link || !target) return;
      wrapper.dataset.bsShortlistReady = 'true';
      const holder = document.createElement('div');
      holder.className = 'bs-shortlist-card-control';
      holder.innerHTML = buttonMarkup(link.href, 'card');
      target.appendChild(holder);
    });
  }

  function injectProductControl(scope = document) {
    const sections = [];
    if (scope instanceof Element && scope.matches('.bsc-simple-product[data-product-url]')) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll('.bsc-simple-product[data-product-url]'));
    sections.forEach((section) => {
      if (section.querySelector('.bs-shortlist-product-control')) return;
      const enquiry = section.querySelector('[data-simple-product-enquiry]')
        || section.querySelector('.product-form__buttons,.product-form');
      if (!enquiry) return;
      const holder = document.createElement('div');
      holder.className = 'bs-shortlist-product-control';
      holder.innerHTML = buttonMarkup(section.dataset.productUrl || location.href, 'product');
      enquiry.insertAdjacentElement('afterend', holder);
    });
  }

  function injectHeaderLink() {
    if (document.querySelector('.bs-shortlist-header-link')) return;
    const icons = document.querySelector('.header__icons') || document.querySelector('header .header-icons') || document.querySelector('header');
    if (!icons) return;
    const link = document.createElement('a');
    link.href = shortlistUrl;
    link.className = 'bs-shortlist-header-link';
    link.setAttribute('aria-label', 'Open product shortlist');
    link.innerHTML = `${iconBookmark()}<span class="bs-shortlist-header-link__label">Shortlist</span><span class="bs-shortlist-count-badge" data-bs-shortlist-count>${products.length}</span>`;
    const cart = icons.querySelector('a[href*="/cart"]');
    cart ? icons.insertBefore(link, cart) : icons.appendChild(link);
  }

  function injectMobileBar() {
    if (document.querySelector('.bs-shortlist-mobile-bar')) return;
    const bar = document.createElement('a');
    bar.href = shortlistUrl;
    bar.className = 'bs-shortlist-mobile-bar';
    bar.innerHTML = `<span class="bs-shortlist-mobile-bar__copy"><strong><span data-bs-shortlist-count>${products.length}</span> products shortlisted</strong><small>Review and download PDF</small></span><b>View</b>`;
    document.body.appendChild(bar);
  }

  function pulseMobileBar() {
    const bar = document.querySelector('.bs-shortlist-mobile-bar');
    if (!bar) return;
    bar.classList.remove('is-updated');
    requestAnimationFrame(() => bar.classList.add('is-updated'));
    window.setTimeout(() => bar.classList.remove('is-updated'), 650);
  }

  function ensureToast() {
    let toast = document.querySelector('.bs-shortlist-toast');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'bs-shortlist-toast';
    toast.innerHTML = `<div><strong>Added to shortlist</strong><span data-bs-toast-copy>Product saved for your proposal.</span></div><a href="${shortlistUrl}">View shortlist</a>`;
    document.body.appendChild(toast);
    return toast;
  }

  function showFeedback(title, copy) {
    if (isMobile()) {
      document.querySelector('.bs-shortlist-toast')?.classList.remove('is-visible');
      pulseMobileBar();
      return;
    }
    const toast = ensureToast();
    toast.querySelector('strong').textContent = title;
    toast.querySelector('[data-bs-toast-copy]').textContent = copy;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  function syncButtons() {
    document.querySelectorAll('.bs-shortlist-add').forEach((button) => {
      const added = indexByIdentity('', button.dataset.bsAddUrl) > -1;
      const state = added ? 'added' : 'idle';
      button.classList.toggle('is-added', added);
      button.setAttribute('aria-pressed', String(added));
      button.setAttribute('aria-label', added ? 'Remove from shortlist' : 'Add to shortlist');
      button.title = added ? 'Shortlisted' : 'Add to shortlist';
      if (button.dataset.bsShortlistState !== state) {
        button.dataset.bsShortlistState = state;
        button.innerHTML = `${added ? iconCheck() : iconBookmark()}<span>${added ? 'Shortlisted' : 'Add to Shortlist'}</span>`;
      }
    });
  }

  function syncCounts() {
    document.querySelectorAll('[data-bs-shortlist-count]').forEach((element) => { element.textContent = products.length; });
    const bar = document.querySelector('.bs-shortlist-mobile-bar');
    const onShortlist = Boolean(document.querySelector('[data-bs-shortlist-page]')) || location.pathname.replace(/\/$/, '') === shortlistUrl.replace(/\/$/, '');
    if (bar) bar.classList.toggle('is-visible', products.length > 0 && !onShortlist);
    document.body.classList.toggle('bs-has-shortlist-mobile-bar', Boolean(bar?.classList.contains('is-visible')));
    document.body.classList.toggle('bs-shortlist-page-active', onShortlist);
  }

  async function mapWithConcurrency(items, limit, iterator) {
    let cursor = 0;
    async function worker() {
      while (cursor < items.length) {
        const current = cursor;
        cursor += 1;
        await iterator(items[current], current);
      }
    }
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  }

  // "Add all to shortlist" -- one button per relevant page (collection, search,
  // AI Gift Finder) that adds every product currently rendered in that page's
  // results to the shortlist in one click. Reuses fetchProduct/persistProducts
  // so behaviour matches the single-product button exactly, just batched.
  const BULK_SCOPES = {
    grid: () => document.querySelector('#product-grid'),
    aigf: () => document.querySelector('[data-aigf-results]')
  };

  function findDesktopProductCount() {
    const standard = [...document.querySelectorAll('.product-count')].find((element) =>
      !element.classList.contains('medium-hide')
      && !element.classList.contains('large-up-hide')
      && !element.classList.contains('product-count-vertical'));
    return standard || document.querySelector('.product-count-vertical') || null;
  }

  function bulkButtonMarkup() {
    return `${iconStack()}<span data-bs-bulk-label>Add all to shortlist</span>`;
  }

  function createBulkButton(scopeKey, slot) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bs-shortlist-bulk-add';
    button.dataset.bsBulkScope = scopeKey;
    button.dataset.bsBulkSlot = slot;
    button.innerHTML = bulkButtonMarkup();
    return button;
  }

  function injectBulkAddControls() {
    const grid = BULK_SCOPES.grid();
    if (grid) {
      const countEl = findDesktopProductCount();
      if (countEl && !countEl.querySelector('.bs-shortlist-bulk-add')) {
        countEl.appendChild(createBulkButton('grid', 'desktop'));
      }
      const mobileFacets = document.querySelector('.mobile-facets__wrapper');
      if (mobileFacets) {
        const next = mobileFacets.nextElementSibling;
        if (!(next && next.classList && next.classList.contains('bs-shortlist-bulk-row'))) {
          const row = document.createElement('div');
          row.className = 'bs-shortlist-bulk-row';
          row.appendChild(createBulkButton('grid', 'mobile'));
          mobileFacets.insertAdjacentElement('afterend', row);
        }
      }
    }
    const aigfHead = document.querySelector('.aigf__results-head');
    if (aigfHead && !aigfHead.querySelector('.bs-shortlist-bulk-add')) {
      aigfHead.appendChild(createBulkButton('aigf', 'aigf'));
    }
  }

  function bulkScopeInfo(scopeKey) {
    const resolver = BULK_SCOPES[scopeKey];
    const container = resolver && resolver();
    if (!container) return null;
    const all = [...container.querySelectorAll('.bs-shortlist-add[data-bs-add-url]')];
    const remaining = all.filter((element) => indexByIdentity('', element.dataset.bsAddUrl) === -1);
    return { total: all.length, remaining: remaining.length };
  }

  function syncBulkControls() {
    document.querySelectorAll('.bs-shortlist-bulk-add').forEach((button) => {
      if (button.classList.contains('is-loading')) return;
      const info = bulkScopeInfo(button.dataset.bsBulkScope);
      const label = button.querySelector('[data-bs-bulk-label]');
      if (!info || !info.total) {
        button.hidden = true;
        return;
      }
      button.hidden = false;
      const complete = info.remaining === 0;
      button.classList.toggle('is-complete', complete);
      if (label) label.textContent = complete ? `All ${info.total} added` : 'Add all to shortlist';
      if (button.dataset.bsBulkIcon !== (complete ? 'check' : 'stack')) {
        button.dataset.bsBulkIcon = complete ? 'check' : 'stack';
        const icon = button.querySelector('svg');
        if (icon) icon.outerHTML = complete ? iconCheck() : iconStack();
      }
    });
  }

  async function bulkAddScope(button) {
    if (pendingButtons.has(button)) return;
    const resolver = BULK_SCOPES[button.dataset.bsBulkScope];
    const container = resolver && resolver();
    if (!container) return;
    const targets = [...container.querySelectorAll('.bs-shortlist-add[data-bs-add-url]')]
      .filter((element) => indexByIdentity('', element.dataset.bsAddUrl) === -1);
    if (!targets.length) return;
    pendingButtons.add(button);
    button.classList.add('is-loading');
    const label = button.querySelector('[data-bs-bulk-label]');
    const total = targets.length;
    let done = 0;
    let added = 0;
    let failed = 0;
    await mapWithConcurrency(targets, 5, async (element) => {
      try {
        const product = await fetchProduct(element.dataset.bsAddUrl);
        if (indexByIdentity(product.id, product.url) === -1) {
          products.push(product);
          added += 1;
        }
      } catch (error) {
        failed += 1;
      } finally {
        done += 1;
        if (label) label.textContent = `Adding ${done} of ${total}…`;
      }
    });
    persistProducts();
    button.classList.remove('is-loading');
    pendingButtons.delete(button);
    const copy = failed
      ? `${added} product${added === 1 ? '' : 's'} added; ${failed} could not be loaded.`
      : `${added} product${added === 1 ? '' : 's'} added to your shortlist.`;
    showFeedback('Added to shortlist', copy);
  }

  async function fetchProduct(url) {
    const path = productPath(url);
    if (!path) throw new Error('Product URL not found');
    const response = await fetch(`${path}.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to load product');
    const data = await response.json();
    const select = document.querySelector('[data-simple-variant]');
    const selectedId = select && productPath(location.href) === path ? select.value : '';
    const variant = (data.variants || []).find((item) => String(item.id) === String(selectedId)) || (data.variants || []).find((item) => item.available) || (data.variants || [])[0] || {};
    let images = (data.images || []).map(normalizeImage).filter(Boolean).slice(0, 5);
    if (!images.length && data.featured_image) images = [normalizeImage(data.featured_image)];
    return migrateProduct({
      id: data.id || data.handle,
      handle: data.handle,
      title: data.title || 'Product',
      sku: variant.sku || '',
      url: canonicalProductUrl(path),
      price: (Number(variant.price || data.price) || 0) / 100,
      images,
      customPrice: null
    });
  }

  async function toggleProduct(button) {
    if (pendingButtons.has(button)) return;
    const url = button.dataset.bsAddUrl;
    pendingButtons.add(button);
    const existing = indexByIdentity('', url);
    if (existing > -1) {
      const [removed] = products.splice(existing, 1);
      persistProducts();
      showFeedback('Removed from shortlist', `${removed.title} was removed.`);
      pendingButtons.delete(button);
      return;
    }
    button.classList.add('is-loading');
    try {
      const product = await fetchProduct(url);
      if (indexByIdentity(product.id, product.url) === -1) products.push(product);
      persistProducts();
      showFeedback('Added to shortlist', `${product.title} is ready for your proposal.`);
    } catch (error) {
      console.error(error);
      showFeedback('Could not add product', 'Please refresh the page and try again.');
    } finally {
      button.classList.remove('is-loading');
      pendingButtons.delete(button);
    }
  }

  function selectedImage(product) {
    const index = Math.min(Number(settings.selectedImages[product.id] || 0), Math.max(0, product.images.length - 1));
    return product.images[index] || '';
  }

  function renderShortlistPage() {
    const page = document.querySelector('[data-bs-shortlist-page]');
    if (!page) return;
    const items = page.querySelector('[data-bs-shortlist-items]');
    const empty = page.querySelector('[data-bs-shortlist-empty]');
    page.querySelectorAll('[data-bs-shortlist-count]').forEach((element) => { element.textContent = products.length; });
    const client = page.querySelector('[data-bs-client-name]');
    if (client && client.value !== settings.clientName) client.value = settings.clientName || '';
    const quantity = page.querySelector('[data-bs-proposal-quantity]');
    if (quantity && quantity.value !== String(settings.proposalQuantity ?? '')) quantity.value = settings.proposalQuantity ?? '';
    const title = page.querySelector('[data-bs-proposal-title]');
    if (title && title.value !== settings.proposalTitle) title.value = settings.proposalTitle || 'Corporate Gift Shortlist';
    const mode = page.querySelector('[data-bs-adjustment-mode]');
    if (mode) mode.value = settings.adjustmentMode || 'none';
    const amount = page.querySelector('[data-bs-adjustment-value]');
    if (amount) {
      amount.value = settings.adjustmentValue || 0;
      amount.disabled = settings.adjustmentMode === 'none';
    }
    const include = page.querySelector('[data-bs-include-prices]');
    if (include) include.checked = settings.includePrices !== false;
    const reseller = page.querySelector('[data-bs-reseller-mode]');
    if (reseller) reseller.checked = Boolean(settings.resellerMode);
    const resellerField = page.querySelector('[data-bs-reseller-logo-field]');
    if (resellerField) resellerField.hidden = !settings.resellerMode;
    syncResellerLogoPreview();

    if (!products.length) {
      items.innerHTML = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    items.innerHTML = products.map((product) => `
      <article class="bs-shortlist-item" data-bs-product-card="${esc(product.id)}">
        <a class="bs-shortlist-item__image-link" href="${esc(product.url)}" target="_blank" rel="noopener" aria-label="Open ${esc(product.title)}">
          <span class="bs-shortlist-item__main"><img src="${esc(selectedImage(product))}" alt="${esc(product.title)}" loading="lazy"></span>
        </a>
        <div class="bs-shortlist-item__copy">
          <div class="bs-shortlist-item__top">
            <h3><a href="${esc(product.url)}" target="_blank" rel="noopener">${esc(product.title)}</a></h3>
            <button type="button" class="bs-shortlist-remove-icon" data-bs-remove="${esc(product.id)}" aria-label="Remove ${esc(product.title)} from shortlist" title="Remove from shortlist"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
          </div>
          <div class="bs-shortlist-item__price">
            <div class="bs-shortlist-price" data-bs-price-for="${esc(product.id)}">${money(effectivePrice(product))} + GST</div>
            <div class="bs-shortlist-fields">
              <label class="bs-shortlist-custom-price"><span>Client offer price</span><input type="number" min="0" step="0.01" inputmode="decimal" placeholder="Use custom price" value="${product.customPrice === null ? '' : esc(product.customPrice)}" data-bs-custom-price data-product-id="${esc(product.id)}"></label>
            </div>
          </div>
        </div>
      </article>`).join('');
  }

  function updatePriceDisplays() {
    products.forEach((product) => {
      const value = document.querySelector(`[data-bs-price-for="${CSS.escape(String(product.id))}"]`);
      if (value) value.textContent = `${money(effectivePrice(product))} + GST`;
    });
  }

  function syncResellerLogoPreview() {
    const upload = document.querySelector('[data-bs-logo-upload]');
    if (!upload) return;
    const img = upload.querySelector('[data-bs-logo-upload-img]');
    const hasLogo = Boolean(settings.resellerLogo);
    upload.classList.toggle('has-logo', hasLogo);
    if (img) img.src = hasLogo ? settings.resellerLogo : '';
  }

  function pagesOf6() {
    const result = [];
    for (let index = 0; index < products.length; index += 6) result.push(products.slice(index, index + 6));
    return result;
  }

  // Recolours a logo image to a solid white silhouette while preserving its
  // alpha channel -- used because the default Bigsmall wordmark is dark navy
  // and disappears against the navy-to-blue header. source-in keeps only the
  // logo's existing opaque shape, so it still reads correctly at any size.
  function whiteSilhouetteData(src, max = 900) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const ratio = Math.min(max / image.naturalWidth, max / image.naturalHeight, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        context.globalCompositeOperation = 'source-in';
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = reject;
      image.src = normalizeImage(src);
    });
  }

  function imageData(src, max = 1400, type = 'jpeg') {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const ratio = Math.min(max / image.naturalWidth, max / image.naturalHeight, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const context = canvas.getContext('2d');
        if (type === 'jpeg') {
          context.fillStyle = '#f4f0e8';
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(type === 'png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.9));
      };
      image.onerror = reject;
      image.src = normalizeImage(src);
    });
  }

  async function loadLogoData() {
    // A reseller's own uploaded logo is used as-is -- it's their brand, not
    // ours, so it shouldn't be recoloured.
    if (settings.resellerMode && settings.resellerLogo) return settings.resellerLogo;
    const logo = document.querySelector('[data-bs-pdf-logo]');
    if (!logo?.src) return null;
    try { return await whiteSilhouetteData(logo.src); }
    catch (error) { console.warn('Unable to load PDF logo', error); return null; }
  }

  /* Option D -- brand card grid: navy-to-blue gradient header with floating
     white client/quantity cards overlapping the edge, a six-card 2-column
     grid per page (image on top, numbered badge, name + price below, a
     blue left accent stripe), and a tinted footer band. This restores the
     "card structure in brand colours" direction from the earlier mockups,
     scaled from 2 cards/page to a denser 6-up grid. */
  const PDF_NAVY = [8, 42, 85];
  const PDF_BLUE = [13, 71, 200];
  const PDF_BLUE_LIGHT = [143, 184, 245];
  const PDF_DARK = [18, 33, 61];
  const PDF_MUTED = [102, 112, 133];
  const PDF_LINE = [220, 229, 242];
  const PDF_FOOTER_BG = [238, 243, 251];
  const PDF_IMAGE_BG = [245, 248, 253];

  function lerpColor(a, b, t) {
    return [
      Math.round(a[0] + (b[0] - a[0]) * t),
      Math.round(a[1] + (b[1] - a[1]) * t),
      Math.round(a[2] + (b[2] - a[2]) * t)
    ];
  }

  // jsPDF 2.5.1 has no native gradient fill, so the header band is
  // approximated with a series of thin vertical strips interpolating
  // between the two brand colours -- reliable across jsPDF versions,
  // unlike shading patterns.
  function drawGradientBand(doc, x, y, w, h, colorStart, colorEnd, steps) {
    const stepWidth = w / steps;
    for (let index = 0; index < steps; index += 1) {
      const t = steps === 1 ? 0 : index / (steps - 1);
      doc.setFillColor(...lerpColor(colorStart, colorEnd, t));
      doc.rect(x + index * stepWidth, y, stepWidth + 0.6, h, 'F');
    }
  }

  function drawHeader(doc, pageWidth, logoData) {
    const headerHeight = 34;
    drawGradientBand(doc, 0, 0, pageWidth, headerHeight, PDF_NAVY, PDF_BLUE, 48);

    if (logoData) {
      const props = doc.getImageProperties(logoData);
      const ratio = Math.min(38 / props.width, 13 / props.height);
      doc.addImage(logoData, 'PNG', 12, 9, props.width * ratio, props.height * ratio, undefined, 'FAST');
    } else if (!settings.resellerMode) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(255, 255, 255);
      doc.text('big', 12, 18);
      doc.setTextColor(...PDF_BLUE_LIGHT);
      doc.text('small', 12 + doc.getTextWidth('big'), 18);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(settings.proposalTitle || 'Corporate Gift Shortlist', pageWidth - 12, 15, { align: 'right', maxWidth: 110 });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...PDF_BLUE_LIGHT);
    doc.text('CURATED GIFTING PROPOSAL', pageWidth - 12, 20, { align: 'right', charSpace: 0.3 });

    const cardY = headerHeight - 9;
    const cardHeight = 15;
    const gap = 6;
    const cardWidth = (pageWidth - 24 - gap) / 2;
    const client = settings.clientName?.trim() || 'Not specified';
    const quantity = settings.proposalQuantity ? formatNumber(settings.proposalQuantity) : 'Not specified';

    [
      { x: 12, label: 'CLIENT', value: client },
      { x: 12 + cardWidth + gap, label: 'QUANTITY', value: quantity }
    ].forEach((item) => {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(item.x, cardY, cardWidth, cardHeight, 2, 2, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...PDF_MUTED);
      doc.text(item.label, item.x + 5, cardY + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_DARK);
      doc.text(doc.splitTextToSize(item.value, cardWidth - 10).slice(0, 1), item.x + 5, cardY + 11);
    });

    return cardY + cardHeight + 8;
  }

  function drawGridCard(doc, product, productImage, rect, number) {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...PDF_LINE);
    doc.setLineWidth(0.3);
    doc.rect(rect.x, rect.y, rect.w, rect.h, 'FD');

    const stripeWidth = 1.1;
    doc.setFillColor(...PDF_BLUE);
    doc.rect(rect.x, rect.y, stripeWidth, rect.h, 'F');

    // Image height is whatever's left after reserving space for the text
    // block below it (gap, up to a 2-line title, gap, price, bottom margin)
    // -- rather than a flat percentage, which left a gap under the price on
    // cards with short, single-line titles.
    const pad = 3;
    const bottomPad = 3;
    const gapImageToText = 5;
    const titleLineHeight = 3.6;
    const maxTitleLines = 2;
    const gapTextToPrice = 3;
    const priceLineHeight = settings.includePrices !== false ? 3.5 : 0;
    const reservedBelowImage = gapImageToText + (maxTitleLines * titleLineHeight) + gapTextToPrice + priceLineHeight + bottomPad;
    const imageHeight = rect.h - pad - reservedBelowImage;
    const imageX = rect.x + stripeWidth + pad;
    const imageY = rect.y + pad;
    const imageW = rect.w - stripeWidth - pad * 2;

    doc.setFillColor(...PDF_IMAGE_BG);
    doc.rect(imageX, imageY, imageW, imageHeight, 'F');

    const props = doc.getImageProperties(productImage);
    const areaW = imageW - 4;
    const areaH = imageHeight - 4;
    const ratio = Math.min(areaW / props.width, areaH / props.height);
    const drawWidth = props.width * ratio;
    const drawHeight = props.height * ratio;
    const drawX = imageX + (imageW - drawWidth) / 2;
    const drawY = imageY + (imageHeight - drawHeight) / 2;
    doc.addImage(productImage, 'JPEG', drawX, drawY, drawWidth, drawHeight, undefined, 'FAST');
    // Image and title link back to the product page -- suppressed in
    // reseller mode so the client-facing PDF doesn't expose Bigsmall URLs.
    if (!settings.resellerMode) doc.link(imageX, imageY, imageW, imageHeight, { url: product.url });

    doc.setFillColor(...PDF_BLUE);
    doc.circle(imageX + 4.5, imageY + 4.5, 3.4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(255, 255, 255);
    doc.text(String(number), imageX + 4.5, imageY + 5.6, { align: 'center' });

    const textX = imageX;
    const textWidth = imageW;
    const textY = imageY + imageHeight + 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_DARK);
    const titleLines = doc.splitTextToSize(product.title, textWidth).slice(0, 2);
    doc.text(titleLines, textX, textY);
    if (!settings.resellerMode) doc.link(textX, textY - 3, textWidth, titleLines.length * 3.6 + 1, { url: product.url });

    if (settings.includePrices !== false) {
      const priceY = textY + titleLines.length * 3.6 + 3;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...PDF_BLUE);
      doc.text(`${money(effectivePrice(product))} + GST`, textX, priceY);
    }
  }

  function cardGridRects(pageWidth, pageHeight, headerBottom, count) {
    const sideMargin = 12;
    const footerHeight = 12;
    const gap = 7;
    const cols = 2;
    const rows = 3;
    const colWidth = (pageWidth - sideMargin * 2 - gap) / cols;
    const usableHeight = pageHeight - headerBottom - footerHeight;
    const rowHeight = (usableHeight - gap * (rows - 1)) / rows;
    const rects = [];
    for (let index = 0; index < count; index += 1) {
      const col = index % cols;
      const row = Math.floor(index / cols);
      rects.push({
        x: sideMargin + col * (colWidth + gap),
        y: headerBottom + row * (rowHeight + gap),
        w: colWidth,
        h: rowHeight
      });
    }
    return rects;
  }

  function drawFooter(doc, pageWidth, pageHeight, pageIndex, totalPages) {
    doc.setFillColor(...PDF_FOOTER_BG);
    doc.rect(0, pageHeight - 11, pageWidth, 11, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...PDF_MUTED);
    doc.text('Prices exclude GST and shipping charges. Client prices are based on the details entered above.', 12, pageHeight - 5.5);
    doc.text(`${pageIndex + 1} / ${totalPages}`, pageWidth - 12, pageHeight - 5.5, { align: 'right' });
  }

  async function downloadPDF(button) {
    const status = document.querySelector('[data-bs-pdf-status]');
    if (!products.length) return;
    if (!window.jspdf?.jsPDF) {
      if (status) status.textContent = 'PDF tools are still loading. Please try again in a moment.';
      return;
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Preparing your proposal…';
    try {
      const width = 216;
      const height = 270;
      const doc = new window.jspdf.jsPDF({ orientation: 'portrait', unit: 'mm', format: [width, height], compress: true });
      const productPages = pagesOf6();
      const images = {};
      const logoData = await loadLogoData();
      for (const product of products) images[product.id] = await imageData(selectedImage(product));
      productPages.forEach((pageProducts, pageIndex) => {
        if (pageIndex) doc.addPage([width, height], 'portrait');
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, width, height, 'F');
        const headerBottom = drawHeader(doc, width, logoData);
        const rects = cardGridRects(width, height, headerBottom, pageProducts.length);
        pageProducts.forEach((product, index) => drawGridCard(doc, product, images[product.id], rects[index], pageIndex * 6 + index + 1));
        drawFooter(doc, width, height, pageIndex, productPages.length);
      });
      const client = (settings.clientName || 'Client').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Client';
      doc.save(`${client}-Bigsmall-Product-Shortlist.pdf`);
      if (status) status.textContent = 'PDF downloaded successfully.';
    } catch (error) {
      console.error(error);
      if (status) status.textContent = 'Unable to create the PDF. Please refresh and try again.';
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function buildSharePayload() {
    const payload = {
      p: products.map((product) => {
        const entry = { h: product.handle, c: Math.round(effectivePrice(product) * 100) / 100 };
        return entry;
      })
    };
    const s = {};
    if (settings.clientName) s.cn = settings.clientName;
    if (settings.proposalQuantity) s.pq = settings.proposalQuantity;
    if (settings.proposalTitle && settings.proposalTitle !== 'Corporate Gift Shortlist') s.pt = settings.proposalTitle;
    if (settings.includePrices === false) s.ip = false;
    if (Object.keys(s).length) payload.s = s;
    return payload;
  }

  async function copyShareLink(button) {
    const status = document.querySelector('[data-bs-pdf-status]');
    if (!products.length) {
      if (status) status.textContent = 'Add products to your shortlist before sharing a link.';
      return;
    }
    button.disabled = true;
    try {
      const encoded = await encodeState(buildSharePayload());
      const url = `${shopUrl}${shortlistUrl}?share=${encoded}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const helper = document.createElement('textarea');
        helper.value = url;
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        document.execCommand('copy');
        helper.remove();
      }
      if (status) status.textContent = 'Shortlist link copied — paste it anywhere to share.';
    } catch (error) {
      console.error(error);
      if (status) status.textContent = 'Could not copy the link. Please try again.';
    } finally {
      button.disabled = false;
    }
  }

  function bindEvents() {
    document.addEventListener('click', (event) => {
      const add = event.target.closest('.bs-shortlist-add');
      if (add) { event.preventDefault(); event.stopPropagation(); toggleProduct(add); return; }
      const bulkAdd = event.target.closest('.bs-shortlist-bulk-add');
      if (bulkAdd) { event.preventDefault(); event.stopPropagation(); bulkAddScope(bulkAdd); return; }
      const remove = event.target.closest('[data-bs-remove]');
      if (remove) {
        const index = products.findIndex((product) => String(product.id) === String(remove.dataset.bsRemove));
        if (index > -1) { products.splice(index, 1); persistProducts(); }
        return;
      }
      const removeLogo = event.target.closest('[data-bs-remove-reseller-logo]');
      if (removeLogo) {
        event.preventDefault();
        event.stopPropagation();
        settings.resellerLogo = null;
        const fileInput = document.querySelector('[data-bs-reseller-logo]');
        if (fileInput) fileInput.value = '';
        persistSettings();
        syncResellerLogoPreview();
        return;
      }
      const clear = event.target.closest('[data-bs-clear-shortlist]');
      if (clear) {
        if (!products.length || window.confirm('Clear all shortlisted products?')) {
          products = [];
          settings.selectedImages = {};
          persistProducts();
          persistSettings();
        }
        return;
      }
      const copyLink = event.target.closest('[data-bs-copy-link]');
      if (copyLink) { copyShareLink(copyLink); return; }
      const download = event.target.closest('[data-bs-download-pdf]');
      if (download) downloadPDF(download);
    });

    document.addEventListener('input', (event) => {
      const target = event.target;
      if (target.matches('[data-bs-client-name]')) {
        settings.clientName = target.value;
        persistSettings();
      } else if (target.matches('[data-bs-proposal-quantity]')) {
        settings.proposalQuantity = integerOrNull(target.value);
        persistSettings();
      } else if (target.matches('[data-bs-proposal-title]')) {
        settings.proposalTitle = target.value;
        persistSettings();
      } else if (target.matches('[data-bs-adjustment-value]')) {
        settings.adjustmentValue = Number(target.value) || 0;
        persistSettings({ updatePrices: true });
      } else if (target.matches('[data-bs-custom-price]')) {
        const product = products.find((item) => String(item.id) === String(target.dataset.productId));
        if (product) {
          product.customPrice = numberOrNull(target.value);
          persistProducts({ render: false });
          const display = document.querySelector(`[data-bs-price-for="${CSS.escape(String(product.id))}"]`);
          if (display) display.textContent = `${money(effectivePrice(product))} + GST`;
        }
      }
    });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-bs-adjustment-mode]')) {
        settings.adjustmentMode = target.value;
        const amount = document.querySelector('[data-bs-adjustment-value]');
        if (amount) amount.disabled = target.value === 'none';
        persistSettings({ updatePrices: true });
      } else if (target.matches('[data-bs-include-prices]')) {
        settings.includePrices = target.checked;
        persistSettings();
      } else if (target.matches('[data-bs-reseller-mode]')) {
        settings.resellerMode = target.checked;
        const field = document.querySelector('[data-bs-reseller-logo-field]');
        if (field) field.hidden = !target.checked;
        persistSettings();
      } else if (target.matches('[data-bs-reseller-logo]')) {
        const file = target.files && target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 500;
            const ratio = Math.min(max / img.naturalWidth, max / img.naturalHeight, 1);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
            canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            settings.resellerLogo = canvas.toDataURL('image/png');
            persistSettings();
            syncResellerLogoPreview();
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY || event.key === SETTINGS_KEY) {
        load();
        syncButtons();
        syncCounts();
        syncBulkControls();
        renderShortlistPage();
      }
    });
  }

  function scan() {
    scanQueued = false;
    injectHeaderLink();
    injectMobileBar();
    injectCardControls();
    injectProductControl();
    injectBulkAddControls();
    syncButtons();
    syncCounts();
    syncBulkControls();
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  async function init() {
    const usedShare = await applySharedStateFromUrl();
    if (!usedShare) load();
    bindEvents();
    scan();
    renderShortlistPage();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes?.length)) scheduleScan();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('resize', syncCounts, { passive: true });
    document.addEventListener('shopify:section:load', scheduleScan);
    window.BigsmallShortlist = { scan: scheduleScan, toggleProduct, getProducts: () => products.slice() };
  }

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init, { once: true }) : init();
})();
